import { keyframes } from '@emotion/react'
import styled from '@emotion/styled/macro'
import * as React from 'react'
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { ReactNode } from 'react'
import { useDialogState } from '~/components/atoms/Dialog'
import { CustomizeOverviewDialog } from '~/components/organisms/CustomizeOverviewDialog'
import { Icon } from '~/components/atoms/Icon'
import { Loading } from '~/components/atoms/Loading'
import { RadioButton, RadioButtonGroup } from '~/components/atoms/RadioButton'
import { ColorSelector } from '~/components/molecules/ColorSelector'
import { Navigation, useScene } from '~/components/organisms/Navigation'
import { MyDesignListPanel } from '~/components/organisms/MyDesignListPanel'
import { PreviewRenderer } from '~/components/organisms/PreviewRenderer'
import { PrintName } from '~/components/organisms/PrintName'
import {
  ShareDialog,
  useShareDialogState
} from '~/components/organisms/ShareDialog'
import { AICustomizationSidebar } from '~/containers/AICustomizationSidebar'

import { SimpleTwoSectionLayout } from '~/components/templates/SimpleTwoSectionLayout'
import type { FeatureFlags } from '~/featureFlags'
import type { Scene } from '~/components/organisms/Navigation'
import type { BootstrapMode } from '~/hooks/useBootstrapMode'
import { useFeatureFlags } from '~/hooks/useFeatureFlags'
import { useFLIP } from '~/hooks/useFLIP'
import { useToast } from '~/hooks/useToast'
import { useViewportState } from '~/hooks/useViewportState'
import type { Color } from '~/types/Color'
import type { Customization, ReadyCustomization } from '~/types/Customization'
import { DirectionX } from '~/types/Design'
import type { Design, View } from '~/types/Design'
import type { Product } from '~/types/Product'
import { PrintType } from '~/types/PrintType'
import { isExpired } from '~/utils/isExpired'
import { toShareUrl, parseReadyCustomization } from '~/utils/customization'
import { mqHorizontal } from '~/utils/mediaQuery'
import { isAIEnabled } from '~/api/ai'

import { ZoomButton } from './ZoomButton'

const PRINT_NAME_PLACEHOLDER = 'NAME'

const AI_OVERLAY_MESSAGES = [
  'AIがぴったりの配色を選択中',
  'AIが色えらびをお手伝い中',
  'AIがデザインを考え中',
  'AIが色の組み合わせを模索中',
  'AIがあなたらしさを色に変換中'
]

interface LoadingStateProps {
  loading: true

  message?: ReactNode
}

interface LoadedStateProps {
  loading?: false

  /**
   * 起動モード
   */
  mode: BootstrapMode

  /**
   * 変更系の操作を行っているかどうか
   *
   * 文字列や Element を指定した場合はメッセージとして表示する。
   */
  mutating?: boolean | ReactNode

  colors: readonly Color[]
  customization: Customization
  design: Design
  product: Product

  onCustom(customization: Customization): void

  /**
   * カスタマイズが完了した状態でマイデザインに保存するボタンが押された
   */
  onSaveAsMyDesign(customization: ReadyCustomization): void

  /**
   * カスタマイズが完了した状態でカートに入れるボタンが押された
   */
  onAddToCart(customization: ReadyCustomization): void

  /**
   * AI利用の計測（計測用）
   */
  onTrackingAIUsage?: () => void

  /**
   * バリデーションエラー発生時の計測（計測用）
   */
  onTrackingValidationErrors?: (errors: Array<{ type: string }>) => void

  /**
   * マイデザイン読み込み時の計測（計測用）
   */
  onTrackingMyDesignLoaded?: () => void
}

type CustomizePageProps = LoadingStateProps | LoadedStateProps

const VIEWPORT_SCALE_MAX = 3

export const CustomizePage = (props: CustomizePageProps) => {
  if (props.loading) {
    return <Loading visible message={props.message} />
  }

  return <LoadedState {...props} />
}

const LoadedState = ({
  mode,
  colors,
  customization,
  design,
  product,
  mutating = false,
  onCustom,
  onSaveAsMyDesign,
  onAddToCart,
  onTrackingAIUsage,
  onTrackingValidationErrors,
  onTrackingMyDesignLoaded
}: LoadedStateProps) => {
  const featureFlags = useFeatureFlags()

  const scene = useScene({ design })

  // 表示する視点のID (型的には `null` になりえるが、基本はない)
  const viewId = useMemo<string | null>(() => {
    switch (scene.current.type) {
      case 'part': {
        // TSはコールバックがいつ実行されるかわからないため、同期的に実行されるコールバックでも
        // プロパティ参照は Narrowing されないためキャスト/アサーションが必要
        const part = design.parts.find(
          (p) => p.id === (scene.current as typeof scene.current).part.id
        )

        return part?.viewId ?? null
      }
      case 'print_logo': {
        return design.printLogo?.id ?? null
      }
      case 'print_name': {
        return design.printName?.id ?? null
      }
    }
  }, [design.parts, design.printLogo, design.printName, scene.current])

  // 表示する視点
  const view = useMemo<View>(() => {
    return design.views.find((view) => view.id === viewId) ?? design.views[0]
  }, [design.views, viewId])

  const rendererRef = useRef<HTMLDivElement>(null)

  // ビュー変更を検出してハイライトアニメーションをトリガーするシグナル
  const [highlightSignal, setHighlightSignal] = useState(0)
  const prevViewRef = useRef(view)

  // ビュー変更を検出
  useEffect(() => {
    if (prevViewRef.current.id !== view.id) {
      prevViewRef.current = view

      // ビューが変更された場合、FLIPアニメーションの完了を待ってハイライトをトリガー
      // FLIPアニメーションは200ms、ease-outなので視覚的な動きは180ms程度で完了
      // 50msのバッファを追加して、アニメーション完了後にハイライトを開始
      const timeoutId = setTimeout(() => {
        setHighlightSignal((prev) => prev + 1)
      }, 250)

      return () => clearTimeout(timeoutId)
    }
  }, [view])

  // 起動時にカスタマイズが指定されている場合も点滅を発生させる
  useEffect(() => {
    // 初回マウント時のみ実行
    const timeoutId = setTimeout(() => {
      setHighlightSignal((prev) => prev + 1)
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [])

  const [overviewDialogProps, overviewDialog] = useDialogState()
  const [shareDialogProps, shareDialog] = useShareDialogState()
  const [activeControlTab, setActiveControlTab] = useState<
    'steps' | 'ai' | 'mydesigns'
  >('steps')

  // 自分でカスタムのステップナビを可視範囲に収めるための再スクロールシグナル
  const [ensureStepVisibleSignal, setEnsureStepVisibleSignal] = useState(0)

  // タブが steps になったタイミング（外部イベント含む）で再スクロールを依頼
  useEffect(() => {
    if (activeControlTab !== 'steps') return
    const raf = requestAnimationFrame(() =>
      setEnsureStepVisibleSignal((v) => v + 1)
    )
    return () => cancelAnimationFrame(raf)
  }, [activeControlTab])

  // AI生成中の見た目状態
  const [isAIGenerating, setIsAIGenerating] = useState(false)
  const [aiOverlayMessage, setAiOverlayMessage] = useState<string | null>(null)

  // --- AIカスタムのエフェメラル状態（タブ切替間で保持、リロードで初期化） ---
  const [aiPrompt, setAiPrompt] = useState<string>('')
  const [aiSelectedColorIds, setAiSelectedColorIds] = useState<Set<string>>(
    new Set()
  )
  const [aiHasGeneratedOnce, setAiHasGeneratedOnce] = useState<boolean>(false)

  const handleAiToggleColor = useCallback((colorId: string) => {
    setAiSelectedColorIds((prev) => {
      const next = new Set(prev)
      if (next.has(colorId)) {
        next.delete(colorId)
      } else {
        next.add(colorId)
      }
      return next
    })
  }, [])

  const handleAiResetColors = useCallback(() => {
    setAiSelectedColorIds(new Set())
  }, [])

  // 一度だけ評価して使い回す（レンダー内の複数箇所で使用）
  const isAIAvailable = useMemo(
    () => featureFlags.enableAICustomization && isAIEnabled(),
    [featureFlags.enableAICustomization]
  )

  // 生成開始時にメッセージを選び、終了時にクリアする
  useEffect(() => {
    if (isAIGenerating) {
      if (!aiOverlayMessage) {
        const idx = Math.floor(Math.random() * AI_OVERLAY_MESSAGES.length)
        setAiOverlayMessage(AI_OVERLAY_MESSAGES[idx])
      }
    } else {
      setAiOverlayMessage(null)
    }
  }, [isAIGenerating, aiOverlayMessage])

  // マイデザインタブを外部イベントで開く
  useEffect(() => {
    const openHandler = () => setActiveControlTab('mydesigns')
    window.addEventListener('my-designs:open', openHandler)
    return () => window.removeEventListener('my-designs:open', openHandler)
  }, [])

  // --- タブパネルの高さをスムーズに遷移（縦向きレイアウトのみ） ---
  const animatedControlsRef = useRef<HTMLDivElement | null>(null)
  const controlsContentRef = useRef<HTMLDivElement | null>(null)
  const [isHorizontal, setIsHorizontal] = useState(false)

  useEffect(() => {
    // mqHorizontal と同条件に合わせる: (orientation: landscape) かつ (min-width: 600px)
    const mql = window.matchMedia(
      '(orientation: landscape) and (min-width: 600px)'
    )
    const update = () => setIsHorizontal(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])

  // 内部状態の変化時（エラー・非同期など）も高さを同期する
  useEffect(() => {
    const wrapper = animatedControlsRef.current
    const content = controlsContentRef.current
    if (!wrapper || !content) {
      return
    }
    if (isHorizontal) {
      wrapper.style.height = ''
      return
    }

    const ro = new ResizeObserver(() => {
      const next = content.getBoundingClientRect().height
      wrapper.style.height = `${next}px`
    })
    ro.observe(content)
    return () => ro.disconnect()
  }, [isHorizontal])

  // シーン種別が変わるとアクション部分の高さが変わるため、プレビューをFLIPアニメーションする
  const { currentScene } = useFLIP({
    props: { currentScene: scene.current },
    flip: {
      first() {
        return rendererRef.current?.getBoundingClientRect()
      },
      last() {
        return rendererRef.current?.getBoundingClientRect()
      },
      invert({ first, last }) {
        if (!first?.height || !last?.height) {
          return { s: 1, ty: 0 }
        }

        return {
          s: first.height / last.height,
          ty: first.y - last.y
        }
      },
      play({ invert: { s, ty } }) {
        rendererRef.current?.animate(
          [
            { transform: `translateY(${ty}px) scale(${s})` },
            { transform: 'translateY(0px) scale(1)' }
          ],
          {
            duration: 200,
            easing: 'ease-out'
          }
        )
      }
    }
  })

  const viewport = useViewportState<HTMLDivElement>({
    disabled: !!mutating,
    scaleMax: VIEWPORT_SCALE_MAX,
    touchDragGestureFingers: featureFlags.requireTwoFingersForDragGesture
      ? 2
      : 1
  })

  // AIでデザイン生成開始時にプレビュー視点を先頭に戻す
  useEffect(() => {
    if (!isAIGenerating) return
    if (scene.scenes[0]) {
      scene.change(scene.scenes[0])
    }
    viewport.reset()
  }, [isAIGenerating, scene.scenes, scene.change, viewport.reset])

  const [viewImageSize, setViewImageSize] = useState<Readonly<{
    width: number
    height: number
  }> | null>(null)

  // 視点が変わったらビューポートの状態をデフォルトにリセットする
  useEffect(() => {
    void view.id
    viewport.reset()
  }, [view.id, viewport.reset])

  // ビュー画像が変わったらサイズをリセットする
  useEffect(() => {
    void view.id
    setViewImageSize(null)
  }, [view.id])

  const viewportOffset = useMemo<{
    x: number
    y: number
    scale: number
  }>(() => {
    const defaultOffset = { x: 0, y: 0, scale: 1 }

    if (currentScene.type === 'part') {
      return defaultOffset
    }

    if (!viewImageSize) {
      return defaultOffset
    }

    const compute = (
      x: number,
      y: number,
      anchorX: DirectionX,
      width: number,
      height: number
    ) => {
      const cx =
        anchorX === DirectionX.Left
          ? x + width / 2
          : anchorX === DirectionX.Right
          ? x - width / 2
          : x
      const cy = y - height / 2
      const scale =
        width > height
          ? viewImageSize.width / 2 / width
          : viewImageSize.height / 2 / height
      return {
        x: 0.5 - cx / viewImageSize.width,
        y: 0.5 - cy / viewImageSize.height,
        scale
      }
    }

    if (currentScene.type === 'print_logo' && design.printLogo) {
      const {
        position: { x, y },
        anchor,
        width,
        height
      } = design.printLogo
      return compute(x, y, anchor.x, width, height)
    }

    if (currentScene.type === 'print_name' && design.printName) {
      const {
        position: { x, y },
        anchor,
        fontSize,
        maxLength
      } = design.printName
      const width = fontSize * maxLength
      const height = fontSize
      return compute(x, y, anchor.x, width, height)
    }

    return defaultOffset
  }, [currentScene.type, design.printLogo, design.printName, viewImageSize])

  // オフセットによるスケールが必要な場合はスケールする
  useEffect(() => {
    viewport.scale(viewportOffset.scale)
  }, [viewport.scale, viewportOffset.scale])

  const printLogoOptions = useMemo<Record<PrintType, boolean>>(() => {
    if (!design.printLogo) {
      return {
        [PrintType.Embossing]: false,
        [PrintType.SilverPressing]: false,
        [PrintType.GoldPressing]: false
      }
    }

    return {
      [PrintType.Embossing]: design.printLogo.printTypes.some(
        (t) => t.printType === 'stamp'
      ),
      [PrintType.SilverPressing]: design.printLogo.printTypes.some(
        (t) => t.printType === 'silver_emboss'
      ),
      [PrintType.GoldPressing]: design.printLogo.printTypes.some(
        (t) => t.printType === 'gold_emboss'
      )
    }
  }, [design.printLogo])

  const availableColors = useMemo<readonly Color[]>(() => {
    if (currentScene.type !== 'part') {
      return []
    }

    const part = design.parts.find((p) => p.id === currentScene.part.id)
    if (!part) {
      return []
    }

    const base = colors.filter(
      (color) =>
        color.material.id === part.material.id &&
        (!color.endAt || !isExpired(color.endAt))
    )

    // 現在選択されている色がフィルタにより除外されている場合でも、
    // 選択状態をUIに反映させるためリストへ含める
    const selectedId = customization.parts[currentScene.part.id]?.color.id
    if (!selectedId) {
      return base
    }
    if (base.some((c) => c.id === selectedId)) {
      return base
    }
    const selectedColor = colors.find((c) => c.id === selectedId)
    return selectedColor ? [...base, selectedColor] : base
  }, [colors, design.parts, currentScene, customization.parts])

  const toast = useToast()

  const readyCustomization = useMemo(
    () => parseReadyCustomization(customization, design),
    [customization, design]
  )

  const parseThen = useCallback(
    (callback: (readyData: ReadyCustomization) => void) => {
      if (!readyCustomization.ok) {
        // 計測: バリデーションエラー発生
        onTrackingValidationErrors?.(readyCustomization.error)

        toast.show({
          variant: 'info',
          title: 'カスタマイズがまだ終わっていません',
          description: (
            <ErrorList>
              {readyCustomization.error.map((error) => {
                switch (error.type) {
                  case 'logo_not_selected':
                    return (
                      <li key="logo_not_selected">
                        <ErrorLinkButton
                          type="button"
                          onClick={() => {
                            toast.hideAll()
                            setActiveControlTab('steps')
                            scene.change({
                              id: 'print_logo',
                              type: 'print_logo' as const
                            })
                          }}
                        >
                          ロゴを選んでください
                        </ErrorLinkButton>
                      </li>
                    )
                  case 'name_not_selected':
                    return (
                      <li key="name_not_selected">
                        <ErrorLinkButton
                          type="button"
                          onClick={() => {
                            toast.hideAll()
                            setActiveControlTab('steps')
                            scene.change({
                              id: 'print_name',
                              type: 'print_name' as const
                            })
                          }}
                        >
                          名入れ刻印の種類を選んでください
                        </ErrorLinkButton>
                      </li>
                    )
                  case 'name_text_empty':
                    return (
                      <li key="name_text_empty">
                        <ErrorLinkButton
                          type="button"
                          onClick={() => {
                            toast.hideAll()
                            setActiveControlTab('steps')
                            scene.change({
                              id: 'print_name',
                              type: 'print_name' as const
                            })
                          }}
                        >
                          名入れ刻印する文字を入力してください
                        </ErrorLinkButton>
                      </li>
                    )
                  case 'name_text_invalid':
                    return (
                      <li key="name_text_invalid">
                        <ErrorLinkButton
                          type="button"
                          onClick={() => {
                            setActiveControlTab('steps')
                            scene.change({
                              id: 'print_name',
                              type: 'print_name' as const
                            })
                          }}
                        >
                          名入れ刻印に利用できない文字が含まれています
                        </ErrorLinkButton>
                      </li>
                    )
                  case 'parts_not_selected':
                    return (
                      <li key="parts_not_selected">
                        <ErrorLinkButton
                          type="button"
                          onClick={() => {
                            setActiveControlTab('steps')
                            const targetPart =
                              design.parts.find(
                                (p) =>
                                  !customization.parts[p.id] ||
                                  !customization.parts[p.id]?.color?.id
                              ) || design.parts[0]
                            if (targetPart) {
                              scene.change({
                                id: `part.${targetPart.id}`,
                                type: 'part',
                                part: {
                                  id: targetPart.id,
                                  name: targetPart.name
                                }
                              })
                            }
                          }}
                        >
                          全てのパーツにカラーを設定してください
                        </ErrorLinkButton>
                      </li>
                    )
                }
              })}
            </ErrorList>
          )
        })

        return
      }

      callback(readyCustomization.data)
    },
    [
      readyCustomization,
      toast.show,
      toast.hideAll,
      scene.change,
      design.parts,
      customization.parts,
      onTrackingValidationErrors
    ]
  )

  // 共有用データをメモ化して生成コストとコード重複を抑制
  const shareData = useMemo(() => {
    const url = toShareUrl(customization, {
      design,
      product,
      baseUrl: process.env.REACT_APP_PORTAL_URL || 'https://joggo.jp'
    })

    return {
      url: url.toString(),
      title: product.name
    }
  }, [customization, design, product])

  const openShare = useCallback(() => {
    shareDialog.open(shareData)
  }, [shareDialog, shareData])

  const copyShareParams = useCallback(() => {
    const url = toShareUrl(customization, {
      design,
      product,
      baseUrl: process.env.REACT_APP_PORTAL_URL || 'https://joggo.jp'
    })
    const params = new URLSearchParams(url.search)
    params.delete('v')
    params.delete('nt')
    navigator.clipboard.writeText(params.toString()).then(() => {
      toast.show({
        variant: 'info',
        title: 'パラメータをコピーしました',
        duration: 2000
      })
    })
  }, [customization, design, product, toast])

  const handleAddToCart = useCallback(() => {
    parseThen((payload) => onAddToCart(payload))
  }, [parseThen, onAddToCart])

  const handleSaveAsMyDesign = useCallback(() => {
    parseThen((payload) => onSaveAsMyDesign(payload))
  }, [parseThen, onSaveAsMyDesign])

  // 安定したコールバック/値を用意して子コンポーネントの再レンダーを抑制
  const onLoadBaseImage = useCallback((img: HTMLImageElement) => {
    setViewImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    })
  }, [])

  const handleColorChange = useCallback(
    (color: Color) => {
      if (currentScene.type !== 'part') {
        return
      }

      onCustom({
        ...customization,
        parts: {
          ...customization.parts,
          [currentScene.part.id]: { color: { id: color.id } }
        }
      })
    },
    [currentScene, customization, onCustom]
  )

  const handlePrintLogoTypeChange = useCallback(
    (printType: PrintType) => {
      onCustom({
        ...customization,
        printLogo: { printType }
      })
    },
    [customization, onCustom]
  )

  const cartButtonLabel = useMemo(
    () =>
      mode.type === 'cart_item_update' || mode.type === 'local_cart_item_update'
        ? 'カートを更新'
        : 'カートに入れる',
    [mode.type]
  )

  // プレビューのtransform文字列をメモ化して再計算を最小化
  const viewTransform = useMemo(
    () =>
      [
        `translate(${viewport.value.tx}px, ${viewport.value.ty}px)`,
        `scale(${viewport.value.scale})`,
        `translate(${viewportOffset.x * 100}%, ${viewportOffset.y * 100}%)`
      ].join(' '),
    [
      viewport.value.tx,
      viewport.value.ty,
      viewport.value.scale,
      viewportOffset.x,
      viewportOffset.y
    ]
  )

  return (
    <Fragment>
      <SimpleTwoSectionLayout
        controls={
          <ControlsColumn>
            {isAIAvailable ? (
              <ControlsTabHeader>
                <ControlsTabButton
                  aria-selected={activeControlTab === 'steps'}
                  onClick={() => setActiveControlTab('steps')}
                  title="カスタマイズしてみる"
                >
                  カスタマイズしてみる
                </ControlsTabButton>
                <ControlsTabButton
                  aria-selected={activeControlTab === 'ai'}
                  onClick={() => setActiveControlTab('ai')}
                  title="AIカスタム"
                >
                  AIカスタム <NewBadge>NEW</NewBadge>
                </ControlsTabButton>
                {featureFlags.enableMyDesignList && (
                  <ControlsTabButton
                    aria-selected={activeControlTab === 'mydesigns'}
                    onClick={() => setActiveControlTab('mydesigns')}
                    title="マイデザイン"
                    aria-label="マイデザイン"
                  >
                    マイデザイン
                  </ControlsTabButton>
                )}
              </ControlsTabHeader>
            ) : null}
            <AnimatedControlsWrapper ref={animatedControlsRef}>
              <ControlsContent ref={controlsContentRef}>
                <ControlsContentBody
                  activeControlTab={activeControlTab}
                  isAIAvailable={isAIAvailable}
                  featureFlags={featureFlags}
                  onSetActiveControlTab={(tab) => {
                    setActiveControlTab(tab)
                    if (tab === 'steps') {
                      // タブをstepsに切り替えた直後にステップメニューの再スクロールを促す
                      setEnsureStepVisibleSignal((v) => v + 1)
                    }
                  }}
                  onAIGeneratingChange={setIsAIGenerating}
                  onTrackingAIUsage={onTrackingAIUsage}
                  onTrackingMyDesignLoaded={onTrackingMyDesignLoaded}
                  onResetViewport={viewport.reset}
                  // エフェメラルAI状態を渡す
                  aiPrompt={aiPrompt}
                  onAiPromptChange={setAiPrompt}
                  aiSelectedColorIds={aiSelectedColorIds}
                  onAiToggleColor={handleAiToggleColor}
                  onAiResetColors={handleAiResetColors}
                  aiHasGeneratedOnce={aiHasGeneratedOnce}
                  onAiHasGeneratedOnceChange={setAiHasGeneratedOnce}
                  product={product}
                  colors={colors}
                  design={design}
                  customization={customization}
                  isReady={readyCustomization.ok}
                  scene={scene}
                  cartButtonLabel={cartButtonLabel}
                  mutating={!!mutating}
                  onCustom={onCustom}
                  onAddToCart={handleAddToCart}
                  onSave={handleSaveAsMyDesign}
                  onShare={openShare}
                  availableColors={availableColors}
                  onChangeColor={handleColorChange}
                  printLogoOptions={printLogoOptions}
                  onChangePrintLogoType={handlePrintLogoTypeChange}
                  printNamePlaceholder={PRINT_NAME_PLACEHOLDER}
                  ensureStepVisibleSignal={ensureStepVisibleSignal}
                />
              </ControlsContent>
            </AnimatedControlsWrapper>
          </ControlsColumn>
        }
        preview={
          <PreviewPane
            featureFlags={featureFlags}
            viewport={viewport}
            viewTransform={viewTransform}
            view={view}
            sceneCurrent={scene.current}
            rendererRef={rendererRef}
            colors={colors}
            design={design}
            customization={customization}
            printNamePlaceholder={PRINT_NAME_PLACEHOLDER}
            isAIGenerating={isAIGenerating}
            aiOverlayMessage={aiOverlayMessage}
            onLoadBaseImage={onLoadBaseImage}
            onOpenOverview={() => overviewDialog.open()}
            mutating={!!mutating}
            onSaveAsMyDesign={handleSaveAsMyDesign}
            onOpenShare={openShare}
            onCopyShareParams={copyShareParams}
            availableColors={availableColors}
            printLogoOptions={printLogoOptions}
            onChangeColor={handleColorChange}
            onChangePrintLogoType={handlePrintLogoTypeChange}
            onCustom={onCustom}
            triggerHighlightSignal={highlightSignal}
          />
        }
      />
      <CustomizeOverviewDialog
        colors={colors}
        design={design}
        customization={customization}
        {...overviewDialogProps}
      />
      <ShareDialog {...shareDialogProps} />
      <MutationLoading
        visible={!!mutating}
        message={typeof mutating === 'string' ? mutating : undefined}
      />
    </Fragment>
  )
}

type GeneratingOverlayProps = {
  visible: boolean
  message: string | null
}

const GeneratingOverlayBase: React.FC<GeneratingOverlayProps> = ({
  visible,
  message
}) => (
  <GeneratingGlowOverlay
    data-visible={visible || undefined}
    aria-hidden={!visible}
  >
    {message ? (
      <GeneratingMessage>
        <Spinner aria-hidden="true" />
        <span>{message}</span>
      </GeneratingMessage>
    ) : null}
  </GeneratingGlowOverlay>
)

const GeneratingOverlay = React.memo(GeneratingOverlayBase)

// プレビュー全体のコンテナ
// 上部に操作エリア分の余白を確保し、内部のオーバーフローは非表示
const PreviewContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 64px;
  overflow: hidden;
`

// プレビュー画像の表示/ズーム用ビューポート（中央寄せ）
const PreviewRenderResultViewport = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 0;
  min-width: 0;

  overflow: visible;
  touch-action: pan-y;
`

// プレビュー画像スプライト（ジェスチャー時のトランジション制御）
const PreviewRenderResultSprite = styled.div`
  aspect-ratio: 1 / 1;
  max-width: 100%;
  max-height: 100%;
  position: relative;

  &[data-gesture-state='none'] {
    transition: transform 0.25s ease-out;
  }
`

const PreviewControlSection = styled.div`
  position: relative;
  padding: 16px 0;

  background-color: hsla(0, 0%, 93%, 0.2);
  backdrop-filter: blur(4px);

  ${mqHorizontal} and (min-height: 600px) {
    padding-bottom: 64px;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

const PreviewRendererWithAnimation = styled(PreviewRenderer)`
  animation: 0.6s 0s ease-out 1 both ${fadeIn};
`

// AI生成中に表示するネオン縁オーバーレイ（プレビュー領域のみ）
const neonBreathe = keyframes`
  0%,100% { opacity: .8 }
  50% { opacity: 1 }
`

const spinnerRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const GeneratingGlowOverlay = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  border-radius: 6px;
  opacity: 0;
  transition: opacity 180ms ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* プレビュー領域のみを曇りガラス風に暗くする */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(16px);
    border-radius: inherit;
  }

  /* ネオン風の縁取り */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &[data-visible] {
    --neon-color: #00e5ff; /* デフォルトはシアン */
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
    &[data-visible]::after {
      animation: none;
    }
  }
`

const GeneratingMessage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 16px;
  text-align: center;
  color: white;
  font-size: 24px;
  letter-spacing: 0.02em;
  line-height: 1.6;
  gap: var(--gutter-medium);
  filter: drop-shadow(0 0 6px color-mix(in oklab, var(--teal-400) 70%, white));
  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: ${spinnerRotate} 0.9s linear infinite;
  filter: drop-shadow(0 0 6px color-mix(in oklab, var(--teal-400) 70%, white));
`

// プレビュー上部の操作バー（ズーム・共有など）
const PreviewActions = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: var(--gutter-medium);
  display: flex;
  justify-content: space-between;
  align-items: center;

  pointer-events: none;
`

const PreviewZoomButton = styled(ZoomButton)`
  pointer-events: all;
`

const PreviewActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--gutter-extra-small);
  padding: var(--gutter-small) var(--gutter-medium);
  font-size: 12px;
  line-height: 1.33;
  letter-spacing: 0.05em;

  background-color: var(--gray-0);
  border-radius: 24px;
  color: var(--black-700);
  pointer-events: all;

  &:focus-visible {
    outline: 1px dotted currentColor;
  }
`

const LogoAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gutter-medium);
`

const PlaceholderLabel = styled.span`
  &[data-hidden] {
    visibility: hidden;
  }
`

const PrintNameWithSpaces = styled(PrintName)`
  max-width: 375px;
  padding: 0 var(--gutter-medium);
  margin: 0 auto;
`

const ErrorList = styled.ul`
  padding-left: 1em;

  list-style: disc;
  text-align: left;
  text-align: start;
`

const ErrorLinkButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  color: inherit;
  text-decoration: underline;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 1px dotted currentColor;
  }
`

const MutationLoading = styled(Loading)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  background-color: hsla(0, 0%, 100%, 0.9);

  z-index: 999;
`

const PreviewActionsRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--gutter-small);
  pointer-events: all;
`

const PreviewIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 16px;
  background-color: var(--gray-0);
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--teal-50);
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid var(--teal-400);
    outline-offset: 2px;
  }

  &:disabled {
    color: var(--gray-400);
    cursor: not-allowed;
  }
`

const CopyParamsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 var(--gutter-small);
  font-size: 11px;
  font-family: monospace;
  background-color: var(--gray-700);
  color: var(--gray-0);
  border-radius: 16px;
  cursor: pointer;
  pointer-events: all;
  white-space: nowrap;

  &:hover {
    background-color: var(--gray-900);
  }
`

const ControlsTabHeader = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: var(--gutter-extra-small);
  align-items: flex-end;
  padding: var(--gutter-small) var(--gutter-small) 0;
  border-bottom: 1px solid var(--gray-300);
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-200);
`

const ControlsTabButton = styled.button`
  flex: 0 1 auto; /* テキストタブは可変幅 */
  min-width: 0;
  padding: 12px 0;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 8px 8px 0 0;
  border: 1px solid transparent;
  border-bottom-color: transparent;
  background: var(--gray-50);
  color: var(--text-main);
  cursor: pointer;
  margin-bottom: -1px; /* ヘッダー下ボーダーと重ねて見た目を繋げる */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;

  &[data-icon-tab='true'] {
    flex: 0 0 40px; /* アイコンタブは固定幅（40px） */
    width: 40px;
  }

  &:hover {
    background: var(--gray-0);
  }

  &[aria-selected='true'] {
    background: var(--white);
    border-color: var(--gray-300);
    border-bottom-color: transparent; /* コンテンツと視覚的に連続させる */
    color: var(--text-main);
    font-weight: var(--bold-weight);
  }

  &:focus-visible {
    outline: 2px solid var(--teal-300);
    outline-offset: 2px;
  }
`

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 8px;
  font-weight: var(--bold-weight);
  color: var(--white);
  background: var(--teal-400);
  line-height: 1;
`

const ControlsColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`

const ControlsContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--gray-300);
  border-top: none; /* アクティブタブと境界線を繋げる */
  background: var(--white);
  @media (min-width: 768px) {
    height: 100%;
  }
  @media (max-width: 768px) {
    max-height: 320px;
  }
`

// 縦向き/モバイルでタブ内容の高さ変化をアニメーションするラッパー
const AnimatedControlsWrapper = styled.div`
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    height: 100%;
  }

  /* ラッパーの高さのみをアニメーション。内部は通常のスクロール */
  transition: height 0.25s ease;
`

// ---- Memoized subcomponents to reduce re-renders ----

type ControlsContentBodyProps = {
  activeControlTab: 'steps' | 'ai' | 'mydesigns'
  isAIAvailable: boolean
  featureFlags: Readonly<FeatureFlags>
  onSetActiveControlTab: (tab: 'steps' | 'ai' | 'mydesigns') => void
  onAIGeneratingChange: (v: boolean) => void
  onTrackingAIUsage?: () => void
  onTrackingMyDesignLoaded?: () => void
  onResetViewport: () => void
  // ステップメニューの再スクロールシグナル
  ensureStepVisibleSignal: number
  // エフェメラルAI状態（外部制御）
  aiPrompt: string
  onAiPromptChange: (v: string) => void
  aiSelectedColorIds: Set<string>
  onAiToggleColor: (id: string) => void
  onAiResetColors: () => void
  aiHasGeneratedOnce: boolean
  onAiHasGeneratedOnceChange: (v: boolean) => void
  product: Product
  colors: readonly Color[]
  design: Design
  customization: Customization
  isReady: boolean
  scene: ReturnType<typeof useScene>
  cartButtonLabel: ReactNode
  mutating: boolean
  onCustom: (customization: Customization) => void
  onAddToCart: () => void
  onSave: () => void
  onShare: () => void
  availableColors: readonly Color[]
  onChangeColor: (color: Color) => void
  printLogoOptions: Record<PrintType, boolean>
  onChangePrintLogoType: (printType: PrintType) => void
  printNamePlaceholder: string
}

const ControlsContentBodyBase: React.FC<ControlsContentBodyProps> = ({
  activeControlTab,
  isAIAvailable,
  featureFlags,
  onSetActiveControlTab,
  onAIGeneratingChange,
  onTrackingAIUsage,
  onTrackingMyDesignLoaded,
  ensureStepVisibleSignal,
  aiPrompt,
  onAiPromptChange,
  aiSelectedColorIds,
  onAiToggleColor,
  onAiResetColors,
  aiHasGeneratedOnce,
  onAiHasGeneratedOnceChange,
  product,
  colors,
  design,
  customization,
  isReady,
  scene,
  cartButtonLabel,
  mutating,
  onResetViewport,
  onCustom,
  onAddToCart,
  onSave,
  onShare,
  availableColors,
  onChangeColor,
  printLogoOptions,
  onChangePrintLogoType,
  printNamePlaceholder
}) => {
  if (activeControlTab === 'ai' && isAIAvailable) {
    return (
      <AICustomizationSidebar
        opened
        onClose={() => onSetActiveControlTab('steps')}
        onApply={onCustom}
        onGeneratingChange={onAIGeneratingChange}
        onTrackingAIUsage={onTrackingAIUsage}
        onProceedToEngraving={() => {
          onSetActiveControlTab('steps')
          const target = scene.scenes.find((s) => s.type === 'print_logo')
          if (target) {
            scene.change(target)
          }
        }}
        // 外部制御のエフェメラルAI状態
        promptExternal={aiPrompt}
        onPromptExternalChange={onAiPromptChange}
        selectedColorIdsExternal={aiSelectedColorIds}
        onToggleColorExternal={onAiToggleColor}
        onResetSelectionExternal={onAiResetColors}
        hasGeneratedOnce={aiHasGeneratedOnce}
        onHasGeneratedOnceChange={onAiHasGeneratedOnceChange}
        product={product}
        colors={colors}
        design={design}
        currentCustomization={customization}
      />
    )
  }

  if (activeControlTab === 'mydesigns' && featureFlags.enableMyDesignList) {
    return (
      <MyDesignListPanel
        product={product}
        design={design}
        colors={colors}
        currentCustomization={customization}
        onApply={(nextCustomization) => {
          // マイデザイン適用時は、プレビュー視点を最初の視点に戻す
          if (scene.scenes[0]) {
            scene.change(scene.scenes[0])
          }
          // ズームや平行移動も初期化
          onResetViewport()
          onCustom(nextCustomization)

          // 計測: マイデザインから読み込み
          onTrackingMyDesignLoaded?.()
        }}
        isReady={isReady}
        onAddToCart={onAddToCart}
        onAddNew={onSave}
      />
    )
  }

  return (
    <Navigation
      disabled={!!mutating}
      cartButtonLabel={cartButtonLabel}
      colors={colors}
      customization={customization}
      isReady={isReady}
      cartDisabled={!isReady}
      scene={scene}
      enableAICustomization={isAIAvailable}
      onAICustomize={() => onSetActiveControlTab('ai')}
      onAddToCart={() => onAddToCart()}
      onSave={() => onSave()}
      onShare={() => onShare()}
      availableColors={availableColors}
      onChangeColor={onChangeColor}
      printLogoOptions={printLogoOptions}
      onChangePrintLogoType={onChangePrintLogoType}
      printNamePlaceholder={printNamePlaceholder}
      design={design}
      onCustom={onCustom}
      ensureActiveStepVisibleSignal={ensureStepVisibleSignal}
    />
  )
}

const ControlsContentBody = React.memo(ControlsContentBodyBase)

type ViewportLike = {
  value: { scale: number; tx: number; ty: number }
  isGesturing: boolean
  scale: (value: number | ((prev: number) => number)) => void
  gestureTarget: React.RefObject<HTMLDivElement>
}

type PreviewPaneProps = {
  featureFlags: Readonly<FeatureFlags>
  viewport: ViewportLike
  viewTransform: string
  view: View
  sceneCurrent: Scene
  rendererRef: React.RefObject<HTMLDivElement>
  colors: readonly Color[]
  design: Design
  customization: Customization
  printNamePlaceholder: string
  isAIGenerating: boolean
  aiOverlayMessage: string | null
  onLoadBaseImage: (img: HTMLImageElement) => void
  onOpenOverview: () => void
  mutating: boolean
  onSaveAsMyDesign: () => void
  onOpenShare: () => void
  onCopyShareParams: () => void
  availableColors: readonly Color[]
  printLogoOptions: Record<PrintType, boolean>
  onChangeColor: (color: Color) => void
  onChangePrintLogoType: (printType: PrintType) => void
  onCustom: (customization: Customization) => void
  triggerHighlightSignal: number
}

const PreviewPaneBase: React.FC<PreviewPaneProps> = ({
  featureFlags,
  viewport,
  viewTransform,
  view,
  sceneCurrent,
  rendererRef,
  colors,
  design,
  customization,
  printNamePlaceholder,
  isAIGenerating,
  aiOverlayMessage,
  onLoadBaseImage,
  onOpenOverview,
  mutating,
  onSaveAsMyDesign,
  onOpenShare,
  onCopyShareParams,
  availableColors,
  printLogoOptions,
  onChangeColor,
  onChangePrintLogoType,
  onCustom,
  triggerHighlightSignal
}) => {
  const [shiftHeld, setShiftHeld] = useState(false)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(true)
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(false)
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  return (
    <PreviewContainer>
      <PreviewRenderResultViewport ref={viewport.gestureTarget}>
        <PreviewRenderResultSprite
          data-gesture-state={viewport.isGesturing ? 'gesture' : 'none'}
          style={{ transform: viewTransform }}
        >
          <PreviewRendererWithAnimation
            key={view.id}
            ref={rendererRef}
            colors={colors}
            design={design}
            view={view}
            currentScene={sceneCurrent}
            customization={customization}
            printNamePlaceholder={printNamePlaceholder}
            onLoadBaseImage={onLoadBaseImage}
            triggerHighlightSignal={triggerHighlightSignal}
          />
          <GeneratingOverlay
            visible={isAIGenerating}
            message={aiOverlayMessage}
          />
        </PreviewRenderResultSprite>
      </PreviewRenderResultViewport>
      <PreviewActions>
        <PreviewZoomButton
          disabled={!!mutating}
          scale={viewport.value.scale}
          min={1}
          max={VIEWPORT_SCALE_MAX}
          step={0.5}
          onScaleChange={viewport.scale}
        />
        {featureFlags.overviewDialog && (
          <PreviewActionButton onClick={onOpenOverview}>
            完成イメージ
          </PreviewActionButton>
        )}
        <PreviewActionsRight>
          <PreviewIconButton
            disabled={!!mutating}
            onClick={onSaveAsMyDesign}
            title="デザインを保存"
          >
            <Icon type="heart" />
          </PreviewIconButton>
          <PreviewIconButton
            disabled={!!mutating}
            onClick={onOpenShare}
            title="デザインをシェア"
          >
            <Icon type="share" />
          </PreviewIconButton>
          {shiftHeld && (
            <CopyParamsButton onClick={onCopyShareParams}>
              params
            </CopyParamsButton>
          )}
        </PreviewActionsRight>
      </PreviewActions>
      <PreviewControlSection />
    </PreviewContainer>
  )
}

const PreviewPane = React.memo(PreviewPaneBase)
